import React, { useState } from 'react';
import { BiPencil, BiPlus } from 'react-icons/bi';
import DeleteProfileDetails from './DeleteProfileDetails';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { UIUserCertificationDetail, UserCertificationDetail } from 'types';
import moment from 'moment';
import { BsLink } from 'react-icons/bs';
import EditProfileCertificateModal from './EditProfileCertificate';
import { deleteUserCertificateFn } from 'src/apis/profile';
import ProfileContentHeader from './ProfileContentHeader';
import { validateUrl } from 'shared-utils';

interface Props {
	title: string;
	editing?: boolean;
	userCertificate?: any;
	profileId?: string;
	id?: string;
	isSelf?: boolean | null;
	isPublicProfile: boolean;
}

const ProfileCertificate = ({
	title,
	isSelf,
	isPublicProfile,
	editing = false,
	userCertificate,
	profileId,
	id,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [isEditing, setIsEditing] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deletingProjectIndex, setDeletingProjectIndex] = useState(-1);
	const [selectedCertificateId, setSelectedCertificateId] = useState<string>('');
	const [deleteModalStates, setDeleteModalStates] = useState<Array<boolean>>(userCertificate.map(() => false));
	const [userCertificates, setUserCertificates] = useState<UserCertificationDetail>({
		cretificateTitle: '',
		cretificateIssuerName: '',
		certificateUniqueId: '',
		issuedOn: new Date(),
	});

	const deleteCertificateDetailFn = useMutation({
		mutationKey: 'deleteCertificate',
		mutationFn: deleteUserCertificateFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Certificate deleted successfully',
				duration: 3000,
				type: 'success',
			});
			const newStates = [...deleteModalStates];
			newStates[deletingProjectIndex] = false;
			setDeleteModalStates(userCertificate.map(() => false));
			setDeletingProjectIndex(-1);
		},
	});

	const handleCertificateDetailsDelete = (certificateId: string, index: number) => {
		setDeletingProjectIndex(index);
		deleteCertificateDetailFn.mutate(certificateId);
	};

	const handleAddCertificate = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		setUserCertificates({
			...userCertificates,
			cretificateTitle: '',
			cretificateIssuerName: '',
			certificateUniqueId: '',
			issuedOn: new Date(),
		});
		setIsEditing(false);
		onOpen();
	};

	return (
		<>
			<ProfileContentHeader
				data={{ name: title, isImp: false }}
				extraContent={
					isSelf ? (
						<div className="ml-auto flex grow-0 items-center justify-end bg-transparent pb-1 mr-1 mt-1">
							{/* Add new certificate button */}
							<div
								onClick={event => handleAddCertificate(event)}
								className="flex items-center justify-center gap-[4px] rounded-[4px] border border-[#C7C9D9] px-[4px] py-[2px] text-[11px] text-[#111928] dark:text-[white] hover:cursor-pointer hover:border-[black] bg-white dark:bg-inherit dark:hover:bg-[white] hover:!text-[black]"
							>
								<BiPlus size={15} />
							</div>
						</div>
					) : null
				}
			/>
			<div id={id} className="flex flex-col gap-2 overflow-hidden rounded-xl bg-white dark:bg-[#1F2A37] relative">
				{userCertificate && userCertificate.length !== 0 ? (
					<div className="flex grow flex-col gap-[5p] px-1">
						{userCertificate.map((user_certificate: UIUserCertificationDetail, index: number) => {
							return (
								<div
									id="certification"
									key={user_certificate._id}
									className="p-3 dark:bg-gray-800 md:group-hover:show-icons-on-hover group w-full whitespace-normal break-words font-inter"
								>
									<div className="flex w-full justify-between">
										{user_certificate.cretificateTitle ? (
											<p className="w-[85%] whitespace-normal break-words text-[16px] font-[500] text-[#28293D] dark:text-[#F2F2F5]">
												{user_certificate.cretificateTitle}
											</p>
										) : null}
										{isSelf ? (
											<div className="edit-delete-icon text-[#28293D] dark:text-[#c2c3db] sm:flex sm:gap-2 md:hidden md:group-hover:flex md:group-hover:gap-3">
												<div>
													<BiPencil
														onClick={e => {
															e.stopPropagation();
															setSelectedCertificateId(user_certificate._id);
															setUserCertificates({
																...userCertificates,
																cretificateTitle: user_certificate.cretificateTitle || '',
																cretificateIssuerName: user_certificate.cretificateIssuerName || '',
																certificateUniqueId: user_certificate.certificateUniqueId || '',
																issuedOn: user_certificate.issuedOn || new Date(),
															});
															setIsEditing(true);
															onOpen();
														}}
														className="cursor-pointer"
													/>
												</div>
												<div>
													<DeleteProfileDetails
														onDelete={handleCertificateDetailsDelete}
														id={user_certificate._id}
														index={index}
														isDeleteModalOpen={deleteModalStates[index]}
														setIsDeleteModalOpen={isOpen => {
															const newStates = [...deleteModalStates];
															newStates[index] = isOpen;
															setDeleteModalStates(newStates);
														}}
														isLoading={deleteCertificateDetailFn.isLoading}
													/>
												</div>
											</div>
										) : null}
									</div>
									{user_certificate.cretificateIssuerName ? (
										<p
											className={`${
												editing ? 'max-w-[90%]' : 'max-w-[100%]'
											} text-[14px] font-[400] text-[#111928] dark:text-[#E4E4EB]`}
										>
											{user_certificate.cretificateIssuerName}
										</p>
									) : null}
									<div
										className={`flex ${
											editing ? 'max-w-[90%]' : 'max-w-[100%]'
										} gap-[4px] text-[12px] font-[400] text-[#111928] dark:text-[#E4E4EB]`}
									>
										<p>{user_certificate.issuedOn && moment(user_certificate.issuedOn).format('YYYY-MM-DD')}</p>
										{user_certificate &&
											user_certificate.certificateUniqueId &&
											(user_certificate?.cretificateUrl || user_certificate.certificateUniqueId) &&
											validateUrl(user_certificate?.cretificateUrl || user_certificate.certificateUniqueId) && (
												<ul className="list-disc pl-[20px]">
													<li
														onClick={() =>
															window.open(
																user_certificate?.cretificateUrl || user_certificate.certificateUniqueId || '',
																'_blank',
															)
														}
														className="flex cursor-pointer items-center gap-1 text-[#7777E7] dark:text-[#6698FA]"
													>
														<BsLink /> <span>Certificate Link</span>
													</li>
												</ul>
											)}
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div className="px-4 py-5 text-center font-inter  text-[14px] font-[500] text-[#28293D] dark:text-[#C7C9D9]">
						{isSelf && !isPublicProfile
							? 'Add your certificates and unlock a world of coding achievements.'
							: "There's nothing to show."}
					</div>
				)}
				<EditProfileCertificateModal
					isEditing={isEditing}
					isOpen={isOpen}
					onClose={onClose}
					userCertificates={userCertificates}
					selectedCertficateId={selectedCertificateId}
				/>
			</div>
		</>
	);
};

export default ProfileCertificate;
